import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import awsAuthService from '../services/aws-auth';
import { BankBalance } from '../types/accounting';
import { BusinessData } from '../types/business';
import * as indexedDBService from '../utils/indexedDBService';
import { awsUserService } from '../services/aws-user-service';
import { usageTracker } from '../services/usage-tracker';
import { isAdminEmail } from '../config/admin';
import { jwtDecode } from 'jwt-decode';
export interface AccountSettings {
  financialYearStart: string;
  openingCashBalance: number;
  openingBankBalances: BankBalance[];
  capitalContribution: number;
  notifications: boolean;
  emailUpdates: boolean;
  theme: 'light' | 'dark';
  numberFormat?: 'normal' | 'hundreds' | 'thousands' | 'lakhs';
}

export interface UserData {
  displayName: string;
  email: string;
  phone?: string;
  role: 'admin' | 'user' | string;
}

// Keep the same UserProfile interface for compatibility
export interface UserProfile extends Omit<UserData, 'phone' | 'email'>, Omit<BusinessData, 'id'> {
  uid: string;
  userId?: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone: string;
  createdAt: Date;
  updatedAt: Date;
  photoURL?: string;
  accountSettings?: AccountSettings;
  lastLogin: Date;
  setupCompleted: boolean;
  purpose: 'business' | 'support' | string;
  dateOfBirth?: string;
  gstRegistered?: 'registered' | 'not_registered';
  gstType?: 'regular' | 'composition' | '';
  gstn?: string | null;
  enableTaxCalculation?: boolean;
  businessActivityType?: string;
  manufacturingActivities?: string[];
  tradingType?: string;
  serviceType?: string;
  socialMediaType?: string;
  address?: string; // Made optional in interface to match usage
  city?: string;
  state?: string;
  pincode?: string;
  tenantId?: string; // Links staff to business owner
  staffList?: any[]; // List of invited and active staff members
}

// Generic User type to replace FirebaseUser
export interface User {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  photoURL: string | null;
  role?: string;
  tenantId?: string;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
}

export interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  initialLoading: boolean;
  isAuthenticated: boolean;
  isSigningUp: boolean;
  error: string | null;
  signup: (email: string, password: string, displayName: string, businessData?: any) => Promise<User>;
  login: (email: string, password: string) => Promise<UserProfile | any | null>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPass: string) => Promise<void>;
  updateUserProfile: (data: Partial<UserProfile>) => Promise<void>;
  refreshAuthToken: () => Promise<boolean>;
  setSigningUp: (signingUp: boolean) => void;
  showEmailVerification: boolean;
  setShowEmailVerification: (show: boolean) => void;
  handleEmailVerified: () => void;
  confirmSignup: (username: string, code: string) => Promise<void>;
  resendSignupConfirmation: (username: string) => Promise<void>;
  recoverStaffProfile: () => Promise<UserProfile | null>;
  completeNewPassword: (cognitoUser: any, newPassword: string, attributes: any) => Promise<UserProfile | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const cognitoUser = awsAuthService.getCurrentUser();

        if (cognitoUser) {
          // Get the session to ensure valid (and get tokens)
          const session = await awsAuthService.getSession();
          if (session.isValid()) {
            const payload = session.getIdToken().payload;
            const jwtToken = session.getIdToken().getJwtToken();
            const decoded: any = jwtDecode(jwtToken);
            const user: User = {
              uid: payload.sub,
              email: payload.email,
              emailVerified: payload.email_verified,
              displayName: payload.name || payload.email.split('@')[0],
              photoURL: null,
              role: decoded['custom:role'] || 'admin',
              tenantId: decoded['custom:tenant_id'],
              getIdToken: async () => jwtToken
            };
            setCurrentUser(user);

            // Load Profile from IndexedDB / DynamoDB
            const profile = await loadUserProfile(user.uid);

            // ── Patch: merge Cognito attributes into userProfile if they are missing or mismatched
            if (profile) {
              let needsUpdate = false;
              const patched = { ...profile };
              
              if (user.role && profile.role !== user.role) {
                patched.role = user.role;
                needsUpdate = true;
              }
              if (user.email && profile.email !== user.email) {
                patched.email = user.email;
                needsUpdate = true;
              }
              if (user.tenantId && profile.tenantId !== user.tenantId) {
                patched.tenantId = user.tenantId;
                needsUpdate = true;
              }
              
              if (needsUpdate) {
                console.log('Patching user profile in initAuth with Cognito attributes');
                setUserProfile(patched as UserProfile);
                indexedDBService.updateUserProfile(user.uid, patched).catch(() => {});
                awsUserService.updateUserProfile(user.uid, { role: patched.role, email: patched.email, tenantId: patched.tenantId }).catch(() => {});
              }
            }

            // Initialize usage tracking
            const isAdmin = isAdminEmail(user.email || '');
            usageTracker.setUser(user.uid, user.email || '', isAdmin);
          }
        }
      } catch (e: any) {
        console.log('No active session or session invalid:', e);
        setCurrentUser(null);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };
    initAuth();
  }, []);

  const loadUserProfile = async (uid: string): Promise<UserProfile | null> => {
    try {
      await indexedDBService.initDB();
      indexedDBService.setCurrentUser(uid);
      let profile = await indexedDBService.getUserProfile(uid);

      if (profile) {
        // Block revoked users immediately
        if (profile.role === 'revoked') {
          console.warn('User account has been revoked. Blocking access.');
          setUserProfile(null);
          return null;
        }

        // Auto-migrate legacy 'user' role to 'admin'
        if (profile.role === 'user') {
          console.log('Migrating legacy user role to admin');
          profile.role = 'admin';
          try {
            await indexedDBService.updateUserProfile(uid, profile);
            await awsUserService.saveUserProfile(profile);
          } catch (migrationErr) {
            console.error('Error during role migration:', migrationErr);
          }
        }

        // Force setupCompleted for super admin
        if (isAdminEmail(profile.email)) {
          profile.setupCompleted = true;
        }

        // Force setupCompleted for staff members who join a workspace
        if (profile.tenantId && profile.role !== 'revoked') {
          profile.setupCompleted = true;
        }

        setUserProfile(profile as UserProfile);
        return profile as UserProfile;
      } else {
        console.log('No local profile found for user:', uid, '- checking AWS...');
        try {
          // Fallback: Check AWS DynamoDB
          const awsProfile = await awsUserService.getUserProfile(uid);

          if (awsProfile) {
            // Block revoked users from AWS profile too
            if (awsProfile.role === 'revoked') {
              console.warn('User is revoked in AWS. Blocking access.');
              return null;
            }

            console.log('Found profile in AWS, syncing to local DB');
            // Save to local DB for future use
            await indexedDBService.updateUserProfile(uid, awsProfile);

            // Force setupCompleted for super admin
            if (isAdminEmail(awsProfile.email)) {
              awsProfile.setupCompleted = true;
            }

            // Force setupCompleted for staff members who join a workspace
            if (awsProfile.tenantId && awsProfile.role !== 'revoked') {
              awsProfile.setupCompleted = true;
            }

            setUserProfile(awsProfile as UserProfile);
            return awsProfile as UserProfile;
          }
        } catch (awsError) {
          console.error('Failed to fetch profile from AWS:', awsError);
        }

        console.log('No profile found locally or in AWS');
        return null;
      }
    } catch (e) {
      console.error('Error loading user profile:', e);
      return null;
    }
  };

  const signup = async (email: string, password: string, displayName: string, businessData?: any): Promise<User> => {
    try {
      setError(null);

      // AWS SignUp
      const attributes = [
        { Name: 'name', Value: displayName },
        { Name: 'email', Value: email }
      ];
      const result = await awsAuthService.signUp(email, password, attributes);

      // Result contains user (CognitoUser) and userSub (UUID)
      // Use getUsername() to be absolutely sure we have the username used for registration
      const uid = result.user.getUsername();
      console.log('Cognito User Created:', uid, 'Sub:', result.userSub);

      const user: User = {
        uid: uid,
        email: email,
        emailVerified: false,
        displayName: displayName,
        photoURL: null,
        getIdToken: async () => '' // No token yet, need to verify
      };

      // Check for Support and Staff Invites
      let userRole: 'user' | 'admin' | 'super_admin' | string = 'admin';
      let tenantId: string | undefined = undefined;
      
      try {
        const supportInvite = await awsUserService.getSupportInvite(email);
        if (supportInvite) {
          console.log('Found support invite for:', email, 'Role:', supportInvite.role);
          userRole = supportInvite.role;
          awsUserService.deleteInvite(email).catch(console.error);
        } else {
          const staffInvite = await awsUserService.getStaffInvite(email);
          if (staffInvite) {
            console.log('Found staff invite for:', email, 'Role:', staffInvite.role, 'Tenant:', staffInvite.tenantId);
            userRole = staffInvite.role;
            tenantId = staffInvite.tenantId;
            
            // Update the business owner's staffList status to 'active'
            awsUserService.getUserProfile(staffInvite.tenantId).then(ownerProfile => {
              if (ownerProfile && ownerProfile.staffList) {
                const updatedList = ownerProfile.staffList.map((s: any) => 
                  s.email === email ? { ...s, status: 'active', uid: uid } : s
                );
                awsUserService.updateUserProfile(staffInvite.tenantId, { staffList: updatedList }).catch(console.error);
              }
            });

            awsUserService.deleteStaffInvite(email).catch(console.error);
          }
        }
      } catch (e) {
        console.warn('Error checking invites:', e);
      }

      // Construct Profile Object
      const userProfile: UserProfile = {
        uid: uid,
        userId: uid,
        email: email,
        tenantId: tenantId,
        displayName,
        role: userRole as any, // Cast to any to accept dynamic roles
        phone: businessData?.phone || '',
        businessName: businessData?.businessName || '',
        businessType: businessData?.businessType || '',
        gstn: businessData?.gstn || null,
        gstRegistered: businessData?.gstRegistered || 'not_registered',
        gstType: businessData?.gstType || '',
        enableTaxCalculation: businessData?.enableTaxCalculation || false,
        businessActivityType: businessData?.businessActivityType || '',
        manufacturingActivities: businessData?.manufacturingActivities || [],
        tradingType: businessData?.tradingType || '',
        serviceType: businessData?.serviceType || '',
        socialMediaType: businessData?.socialMediaType || '',
        address: businessData?.address || '',
        city: businessData?.city || '',
        state: businessData?.state || '',
        pincode: businessData?.pincode || '',
        purpose: userRole === 'admin' || userRole === 'user' ? 'business' : 'support',
        dateOfBirth: businessData?.dateOfBirth || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLogin: new Date(),
        setupCompleted: isAdminEmail(email) || !!tenantId || userRole !== 'admin' ? true : (businessData ? true : false),
        accountSettings: {
          financialYearStart: '04-01',
          openingCashBalance: 0,
          openingBankBalances: [],
          capitalContribution: 0,
          notifications: true,
          emailUpdates: true,
          theme: 'light'
        }
      };

      // Save Profile to IndexedDB (as "pending" or just save it)
      // Since verification is next, we can save it.
      // Save Profile to IndexedDB and AWS
      try {
        await indexedDBService.initDB();
        await indexedDBService.updateUserProfile(uid, userProfile);

        // SYNC TO AWS: Ensure the profile exists in the cloud immediately
        await awsUserService.saveUserProfile(userProfile);

        // Also save to localStorage as backup
        localStorage.setItem(`pending_profile_${uid}`, JSON.stringify(userProfile));

      } catch (e) {
        console.error('Failed to save local/cloud profile:', e);
      }

      setUserProfile(userProfile);
      return user;
    } catch (err: any) {
      console.error('Error in signup:', err);
      setError(err.message || 'Failed to create account');
      throw err;
    }
  };

  const login = async (email: string, password: string): Promise<UserProfile | any | null> => {
    try {
      setError(null);
      const result = await awsAuthService.signIn(email, password);

      if (result.newPasswordRequired) {
        return { 
          newPasswordRequired: true, 
          user: result.user, 
          userAttributes: result.userAttributes 
        };
      }

      // Use ID Token payload — it contains email, sub, etc.
      const idTokenPayload = result.getIdToken().payload;
      const accessPayload = result.getAccessToken().payload;
      const jwtToken = result.getIdToken().getJwtToken();
      const decoded: any = jwtDecode(jwtToken);

      const user: User = {
        uid: accessPayload.sub,
        email: idTokenPayload.email || email,
        emailVerified: idTokenPayload.email_verified,
        displayName: idTokenPayload.name || email.split('@')[0],
        photoURL: null,
        role: decoded['custom:role'] || 'admin',
        tenantId: decoded['custom:tenant_id'],
        getIdToken: async () => jwtToken
      };

      setCurrentUser(user);

      // Check for pending profile in localStorage (from signup)
      const pendingProfileStr = localStorage.getItem(`pending_profile_${user.uid}`);
      if (pendingProfileStr) {
        const pending = JSON.parse(pendingProfileStr);
        await indexedDBService.updateUserProfile(user.uid, pending);
        localStorage.removeItem(`pending_profile_${user.uid}`);
      }

      let profile = await loadUserProfile(user.uid);

      // ─── CRITICAL FIX: Email-key profile migration ─────────────────────────
      // Old signup bug: profiles were saved with userId = email (not UUID sub).
      // If profile not found by UUID, try fetching by email from AWS.
      if (!profile) {
        try {
          console.log('Profile not found by UUID. Trying email-key fallback...');
          const emailKeyedProfile = await awsUserService.getUserProfile(email.toLowerCase());
          if (emailKeyedProfile && emailKeyedProfile.email?.toLowerCase() === email.toLowerCase()) {
            console.log('Found profile stored under email key. Migrating to UUID key...');
            // Rebuild profile with the correct UUID key
            const migratedProfile = {
              ...emailKeyedProfile,
              uid: user.uid,
              userId: user.uid,
              updatedAt: new Date().toISOString(),
            };
            // Save with correct UUID key
            await awsUserService.saveUserProfile(migratedProfile);
            await indexedDBService.updateUserProfile(user.uid, migratedProfile);
            // Set as current profile
            profile = migratedProfile as UserProfile;
            setUserProfile(profile);
          }
        } catch (emailLookupErr) {
          console.warn('Email-key fallback lookup failed:', emailLookupErr);
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // Check for pending staff invite on login
      try {
        const staffInvite = await awsUserService.getStaffInvite(email);
        if (staffInvite) {
          console.log('Found staff invite during login for:', email, 'Role:', staffInvite.role, 'Tenant:', staffInvite.tenantId);
          
          if (!profile) {
            profile = {
              uid: user.uid,
              userId: user.uid,
              email: email,
              displayName: user.displayName || email.split('@')[0],
              role: staffInvite.role as any,
              tenantId: staffInvite.tenantId,
              setupCompleted: true,
              createdAt: new Date(),
              updatedAt: new Date(),
              lastLogin: new Date(),
              phone: ''
            } as UserProfile;
          } else {
            profile.role = staffInvite.role;
            profile.tenantId = staffInvite.tenantId;
            profile.setupCompleted = true;
          }
            
          // Save updated profile
          await awsUserService.saveUserProfile(profile);
          await indexedDBService.updateUserProfile(user.uid, profile);
          setUserProfile(profile);
          
          // Update owner's staffList status to 'active'
          awsUserService.getUserProfile(staffInvite.tenantId).then(ownerProfile => {
            if (ownerProfile && ownerProfile.staffList) {
              const updatedList = ownerProfile.staffList.map((s: any) => 
                s.email?.toLowerCase() === email.toLowerCase() ? { ...s, status: 'active', uid: user.uid } : s
              );
              awsUserService.updateUserProfile(staffInvite.tenantId, { staffList: updatedList }).catch(console.error);
            }
          });

          // Delete the invite since it's consumed
          awsUserService.deleteStaffInvite(email).catch(console.error);

        } else if (!profile) {
          // AUTO-HEALING: Scan all users' staffLists to find this email
          console.log('No invite found and no profile. Running auto-recovery scan...');
          try {
            const allUsers = await awsUserService.listAllUsers();
            for (const userRecord of allUsers) {
              // Skip non-user records (invites etc.)
              if (!userRecord.staffList || !Array.isArray(userRecord.staffList)) continue;
              if (userRecord.userId?.includes(':')) continue; // skip invite records
              
              const staffEntry = userRecord.staffList.find((s: any) => s.email?.toLowerCase() === email.toLowerCase());
              if (staffEntry) {
                console.log('Auto-recovered: found email in staffList of owner:', userRecord.userId);
                profile = {
                  uid: user.uid,
                  userId: user.uid,
                  email: email,
                  displayName: user.displayName || email.split('@')[0],
                  role: staffEntry.role as any,
                  tenantId: userRecord.userId,
                  setupCompleted: true,
                  createdAt: new Date(),
                  updatedAt: new Date(),
                  lastLogin: new Date(),
                  phone: ''
                } as UserProfile;
                
                await awsUserService.saveUserProfile(profile);
                await indexedDBService.updateUserProfile(user.uid, profile);
                setUserProfile(profile);
                
                // Mark as active in owner's list
                const updatedList = userRecord.staffList.map((s: any) => 
                  s.email?.toLowerCase() === email.toLowerCase() ? { ...s, status: 'active', uid: user.uid } : s
                );
                await awsUserService.updateUserProfile(userRecord.userId, { staffList: updatedList });
                break;
              }
            }
          } catch (scanErr) {
            console.warn('Auto-recovery scan failed:', scanErr);
          }
        }
      } catch (e) {
        console.warn('Error checking invites during login:', e);
      }

      // Re-fetch profile to ensure we have the latest state
      if (!profile) {
        profile = await loadUserProfile(user.uid);
      }

      if (profile) {
        let needsUpdate = false;
        const patched = { ...profile };
        
        if (user.role && profile.role !== user.role) {
          patched.role = user.role;
          needsUpdate = true;
        }
        if (user.email && profile.email !== user.email) {
          patched.email = user.email;
          needsUpdate = true;
        }
        if (user.tenantId && profile.tenantId !== user.tenantId) {
          patched.tenantId = user.tenantId;
          needsUpdate = true;
        }
        
        if (needsUpdate) {
          console.log('Patching user profile in login with Cognito attributes');
          profile = patched as UserProfile;
          setUserProfile(profile);
          indexedDBService.updateUserProfile(user.uid, patched).catch(() => {});
          awsUserService.updateUserProfile(user.uid, { role: patched.role, email: patched.email, tenantId: patched.tenantId }).catch(() => {});
        }
      }

      // Sync business details from AWS to IndexedDB
      await syncBusinessDetailsFromAWS(user.uid);

      // Update last login
      if (profile) {
        await updateUserProfile({ lastLogin: new Date() });
      }

      return profile;

    } catch (err: any) {
      console.error('Error in login:', err);
      throw err;
    }
  };

  // Sync business details from AWS to IndexedDB
  const syncBusinessDetailsFromAWS = async (uid: string): Promise<void> => {

    try {
      console.log('Syncing business details from AWS to IndexedDB...');

      // Fetch user profile from AWS (which contains businessDetails)
      const awsProfile = await awsUserService.getUserProfile(uid);

      if (awsProfile && awsProfile.businessDetails) {
        console.log('Found business details in AWS profile, syncing to IndexedDB...');

        const businessDetails = awsProfile.businessDetails;

        // Transform AWS businessDetails to IndexedDB format
        const indexedDBBusinessData = {
          id: uid,
          userId: uid,
          uid: uid,
          name: businessDetails.businessName || '',
          businessName: businessDetails.businessName || '',
          address: businessDetails.officeAddress
            ? `${businessDetails.officeAddress.line1}${businessDetails.officeAddress.line2 ? ', ' + businessDetails.officeAddress.line2 : ''}, ${businessDetails.officeAddress.city}, ${businessDetails.officeAddress.state} - ${businessDetails.officeAddress.pincode}`
            : '',
          city: businessDetails.officeAddress?.city || '',
          state: businessDetails.officeAddress?.state || '',
          pincode: businessDetails.officeAddress?.pincode || '',
          gstin: businessDetails.gstn || '',
          GSTIN: businessDetails.gstn || '',
          phone: businessDetails.authorizedRepresentative?.contact || '',
          email: awsProfile.email || '',
          entityType: businessDetails.entityType || '',
          natureOfBusiness: businessDetails.natureOfBusiness || '',
          turnover: businessDetails.turnover || '',
          gstRegistered: businessDetails.gstRegistered || 'not_registered',
          createdAt: awsProfile.createdAt || new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        // Save to IndexedDB
        await indexedDBService.initDB();
        const IndexedDBService = (await import('../services/IndexedDBService')).default;
        await IndexedDBService.saveBusinessDetails(indexedDBBusinessData);

        console.log('Business details synced successfully from AWS to IndexedDB');
      } else {
        console.log('No business details found in AWS profile');
      }
    } catch (error) {
      console.error('Failed to sync business details from AWS:', error);
      // Don't throw - this is a non-critical sync operation
      console.warn('Continuing despite business details sync error');
    }
  };

  const logout = async () => {
    try {
      setError(null);
      awsAuthService.signOut();
      setCurrentUser(null);
      setUserProfile(null);
    } catch (err: any) {
      console.error('Error in logout:', err);
      setError(err.message || 'Failed to log out');
      throw err;
    }
  };

  const refreshAuthToken = async (): Promise<boolean> => {
    try {
      const session = await awsAuthService.getSession();
      return session && session.isValid();
    } catch (err) {
      return false;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      await awsAuthService.forgotPassword(email);
    } catch (err: any) {
      console.error('Error in resetPassword:', err);
      setError(err.message || 'Failed to send reset code');
      throw err;
    }
  };

  const confirmPasswordReset = async (email: string, code: string, newPass: string) => {
    try {
      setError(null);
      await awsAuthService.confirmPassword(email, code, newPass);
    } catch (err: any) {
      console.error('Error in confirmPasswordReset:', err);
      setError(err.message || 'Failed to reset password');
      throw err;
    }
  };

  const updateUserProfile = async (data: Partial<UserProfile>): Promise<void> => {
    if (!currentUser) return;
    try {
      await indexedDBService.updateUserProfile(currentUser.uid, {
        ...data,
        // Ensure setupCompleted is preserved if not explicitly provided
        setupCompleted: data.setupCompleted !== undefined
          ? data.setupCompleted
          : (userProfile?.setupCompleted ?? true)
      });
      const updated = await indexedDBService.getUserProfile(currentUser.uid);
      if (updated) setUserProfile(updated as UserProfile);
    } catch (e: any) {
      throw new Error('Failed to update profile: ' + e.message);
    }
  };

  const setSigningUp = (signingUp: boolean) => {
    setIsSigningUp(signingUp);
  };

  const handleEmailVerified = () => {
    setShowEmailVerification(false);
  };

  const confirmSignup = async (username: string, code: string): Promise<void> => {
    return awsAuthService.confirmRegistration(username, code);
  };

  const resendSignupConfirmation = async (username: string): Promise<void> => {
    return awsAuthService.resendConfirmationCode(username);
  };

  /**
   * Recovers a staff profile when login() failed to create one.
   * Scans for a pending invite or owner staffList entry, builds the profile,
   * saves it to DynamoDB + IndexedDB, and sets it in React state.
   */
  const recoverStaffProfile = async (): Promise<UserProfile | null> => {
    if (!currentUser) return null;
    const email = currentUser.email || '';
    if (!email) return null;

    try {
      // 1. Check pending staff invite
      const staffInvite = await awsUserService.getStaffInvite(email);
      if (staffInvite) {
        const recovered: UserProfile = {
          uid: currentUser.uid,
          userId: currentUser.uid,
          email: email,
          displayName: currentUser.displayName || email.split('@')[0],
          role: staffInvite.role as any,
          tenantId: staffInvite.tenantId,
          setupCompleted: true,
          createdAt: new Date(),
          updatedAt: new Date(),
          lastLogin: new Date(),
          phone: ''
        } as UserProfile;

        await awsUserService.saveUserProfile(recovered);
        await indexedDBService.updateUserProfile(currentUser.uid, recovered);
        setUserProfile(recovered);
        awsUserService.deleteStaffInvite(email).catch(console.error);

        // Mark active in owner's list
        awsUserService.getUserProfile(staffInvite.tenantId).then((ownerProfile: any) => {
          if (ownerProfile?.staffList) {
            const updated = ownerProfile.staffList.map((s: any) =>
              s.email?.toLowerCase() === email.toLowerCase()
                ? { ...s, status: 'active', uid: currentUser.uid }
                : s
            );
            awsUserService.updateUserProfile(staffInvite.tenantId, { staffList: updated }).catch(console.error);
          }
        });

        return recovered;
      }

      // 2. Fallback: scan all owners' staffLists
      const allUsers = await awsUserService.listAllUsers();
      for (const ownerRecord of allUsers) {
        if (ownerRecord.userId?.includes(':')) continue;
        if (!ownerRecord.staffList || !Array.isArray(ownerRecord.staffList)) continue;
        const staffEntry = ownerRecord.staffList.find((s: any) =>
          s.email?.toLowerCase() === email.toLowerCase()
        );
        if (staffEntry) {
          const recovered: UserProfile = {
            uid: currentUser.uid,
            userId: currentUser.uid,
            email: email,
            displayName: currentUser.displayName || email.split('@')[0],
            role: staffEntry.role as any,
            tenantId: ownerRecord.userId,
            setupCompleted: true,
            createdAt: new Date(),
            updatedAt: new Date(),
            lastLogin: new Date(),
            phone: ''
          } as UserProfile;

          await awsUserService.saveUserProfile(recovered);
          await indexedDBService.updateUserProfile(currentUser.uid, recovered);
          setUserProfile(recovered);

          const updatedList = ownerRecord.staffList.map((s: any) =>
            s.email?.toLowerCase() === email.toLowerCase()
              ? { ...s, status: 'active', uid: currentUser.uid }
              : s
          );
          awsUserService.updateUserProfile(ownerRecord.userId, { staffList: updatedList }).catch(console.error);

          return recovered;
        }
      }
    } catch (err) {
      console.error('recoverStaffProfile failed:', err);
    }

    return null;
  };

  const completeNewPassword = async (cognitoUser: any, newPassword: string, attributes: any): Promise<UserProfile | null> => {
    try {
      const result = await awsAuthService.completeNewPassword(cognitoUser, newPassword, attributes);
      // After complete, we'll have tokens
      const idTokenPayload = result.getIdToken().payload;
      const accessPayload = result.getAccessToken().payload;
      const jwtToken = result.getIdToken().getJwtToken();
      const decoded: any = jwtDecode(jwtToken);

      const user: User = {
        uid: accessPayload.sub,
        email: idTokenPayload.email || cognitoUser.getUsername(),
        emailVerified: idTokenPayload.email_verified,
        displayName: idTokenPayload.name || cognitoUser.getUsername().split('@')[0],
        photoURL: null,
        role: decoded['custom:role'] || 'admin',
        tenantId: decoded['custom:tenant_id'],
        getIdToken: async () => jwtToken
      };

      setCurrentUser(user);
      
      let profile = await loadUserProfile(user.uid);
      
      // Auto-create dummy profile for staff so UI doesn't crash if it expects profile
      if (!profile && user.tenantId) {
         profile = {
           uid: user.uid,
           userId: user.uid,
           email: user.email!,
           displayName: user.displayName!,
           role: user.role as any,
           tenantId: user.tenantId,
           setupCompleted: true,
           createdAt: new Date(),
           updatedAt: new Date(),
           lastLogin: new Date(),
           phone: ''
         } as UserProfile;
         setUserProfile(profile);
         
         // Persist immediately to prevent incomplete onboarding profile issues
         indexedDBService.updateUserProfile(user.uid, profile).catch(console.error);
         awsUserService.saveUserProfile(profile).catch(console.error);
      }

      return profile;
    } catch (err: any) {
      console.error('Error in completeNewPassword:', err);
      throw err;
    }
  };

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    initialLoading,
    isAuthenticated: !!currentUser,
    isSigningUp,
    error,
    signup,
    login,
    logout,
    resetPassword,
    confirmPasswordReset,
    updateUserProfile,
    refreshAuthToken,
    setSigningUp,
    showEmailVerification,
    setShowEmailVerification,
    handleEmailVerified,
    confirmSignup,
    resendSignupConfirmation,
    recoverStaffProfile,
    completeNewPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider; 
